/**
 * Left rail — accordion groups + direct links.
 *
 * Driven by SIDEBAR_NAV (a separate config from the TopNav sections), so the
 * two navs show different items — one home per page, no duplication. The
 * sidebar owns: Org Dashboard, Asset Discovery, Endpoint Inventory,
 * Resource Monitoring, Alerts Panel, and Administration (org_admin only).
 *
 * Collapsed mode shows one icon per group; hovering opens a flyout with the
 * group's section title + sub-items. Portal-rendered to document.body so it
 * escapes the sidebar's overflow: hidden.
 */
import { useEffect, useRef, useState }    from "react";
import { createPortal }                   from "react-dom";
import { NavLink, useLocation }           from "react-router-dom";
import styles                             from "./Sidebar.module.scss";
import { SIDEBAR_NAV, type SidebarGroup } from "@app/navigation/sidebarNav";
import { useAuth }                        from "@features/auth/AuthContext";
import { preloadRoute }                   from "@app/routes/preload";
import { SHELL }                          from "@shared/config";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

/** Small delay before closing so the mouse can travel from the trigger
 *  icon into the flyout body without the panel snapping shut mid-transit. */
const CLOSE_DELAY_MS = 120;

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  // Collapsed-mode flyout state — one hovered group at a time. Storing top/left
  // instead of the element ref means the flyout won't drift if the group's
  // rect changes mid-hover.
  const [hoverState, setHoverState] = useState<{ id: string; top: number; left: number } | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const navRef = useRef<HTMLElement>(null);

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setHoverState(null), CLOSE_DELAY_MS);
  };
  const openFlyout = (id: string, el: HTMLElement) => {
    if (!collapsed) return;
    cancelClose();
    const r = el.getBoundingClientRect();
    setHoverState({ id, top: r.top, left: r.right });
  };

  // Close the flyout when the sidebar expands (irrelevant), on unmount, or
  // when the user scrolls the nav (trigger position becomes stale).
  useEffect(() => {
    if (!collapsed && hoverState) setHoverState(null);
  }, [collapsed, hoverState]);
  useEffect(() => () => cancelClose(), []);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const onScroll = () => setHoverState(null);
    nav.addEventListener("scroll", onScroll, { passive: true });
    return () => nav.removeEventListener("scroll", onScroll);
  }, []);

  const role  = user?.role ?? "";
  const scope = user?.scope_level ?? "";

  // Filter by role AND by scope_level. An item passes when:
  //   • it has no `roles` restriction, OR the current user's role matches
  //   AND
  //   • it has no `scopes` restriction, OR the current user's scope matches
  // Group-admin-only entries (Organizations, Group Dashboard) use `scopes`;
  // org_admin-only entries (Administration group) use `roles`.
  const visibleGroups = SIDEBAR_NAV.filter((g) => {
    const roleOk  = !g.roles  || g.roles.includes(role);
    const scopeOk = !g.scopes || g.scopes.includes(scope as "platform" | "group" | "org");
    return roleOk && scopeOk;
  });

  const isActive = (href: string) => pathname === href;
  const groupHasActive = (g: SidebarGroup) => {
    if (g.href && isActive(g.href)) return true;
    return g.items?.some((i) => isActive(i.href)) ?? false;
  };

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isOpen = (id: string) => openGroups.has(id);

  const hoveredGroup = hoverState ? visibleGroups.find((g) => g.id === hoverState.id) ?? null : null;

  return (
    <>
      <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}>
        <div className={styles.top}>
          {/* <span className={styles.brandMark}>{SHELL.brandInitial}</span> */}
          {!collapsed && <span className={styles.brandName}>{SHELL.product}</span>}
          <button
            className={styles.toggle}
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {/* Single static icon — SCSS rotates it 180° via .collapsed .toggle i.
                Previously the JSX swapped between fa-angles-right and fa-angles-left,
                which combined with the CSS rotation made both states render the same
                visual (left-pointing). */}
            <i className="fas fa-angles-left" />
          </button>
        </div>

        <nav className={styles.nav} ref={navRef}>
          {visibleGroups.map((group) => {
            const active = groupHasActive(group);

            // Hover handlers are only wired in collapsed mode — expanded mode
            // uses inline accordions and doesn't need the flyout at all.
            const flyoutHandlers = collapsed
              ? {
                  onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) =>
                    openFlyout(group.id, e.currentTarget),
                  onMouseLeave: scheduleClose,
                }
              : {};

            // Direct link (no sub-items): the header IS the link
            if (group.href && !group.items) {
              return (
                <div
                  key={group.id}
                  className={`${styles.group} ${active ? styles.hasActive : ""}`}
                  {...flyoutHandlers}
                >
                  <NavLink
                    to={group.href}
                    className={`${styles.directLink} ${active ? styles.directActive : ""}`}
                    aria-label={group.label}
                    title={collapsed ? undefined : group.label}
                    onMouseEnter={() => preloadRoute(group.href!)}
                    onFocus={() => preloadRoute(group.href!)}
                  >
                    <span className={styles.gIc}>
                      <i className={`fas ${group.icon}`} />
                    </span>
                    {!collapsed && <span className={styles.gLabel}>{group.label}</span>}
                  </NavLink>
                </div>
              );
            }

            // Expandable group
            const open = isOpen(group.id) || active;
            return (
              <div
                key={group.id}
                className={`${styles.group} ${open ? styles.open : ""} ${active ? styles.hasActive : ""}`}
                {...flyoutHandlers}
              >
                <button
                  className={styles.groupHd}
                  onClick={() => toggleGroup(group.id)}
                  aria-label={group.label}
                  title={collapsed ? undefined : group.label}
                >
                  <span className={styles.gIc}>
                    <i className={`fas ${group.icon}`} />
                  </span>
                  {!collapsed && (
                    <>
                      <span className={styles.gLabel}>{group.label}</span>
                      <i className={`fas fa-chevron-down ${styles.gChev}`} />
                    </>
                  )}
                </button>

                {!collapsed && group.items && (
                  <div className={styles.groupItems}>
                    {group.items.map((item) =>
                      item.soon ? (
                        <span
                          key={item.id}
                          className={`${styles.sbItem} ${styles.itemSoon}`}
                          title="Coming soon"
                        >
                          <span className={styles.iIc}>
                            <i className={`fas ${item.icon}`} />
                          </span>
                          <span className={styles.iLabel}>{item.label}</span>
                          <span className={styles.soonTag}>Soon</span>
                        </span>
                      ) : (
                        <NavLink
                          key={item.id}
                          to={item.href}
                          title={item.label}
                          className={({ isActive: a }) =>
                            `${styles.sbItem} ${a ? styles.itemActive : ""}`
                          }
                          onMouseEnter={() => preloadRoute(item.href)}
                          onFocus={() => preloadRoute(item.href)}
                        >
                          <span className={styles.iIc}>
                            <i className={`fas ${item.icon}`} />
                          </span>
                          <span className={styles.iLabel}>{item.label}</span>
                        </NavLink>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Collapsed-mode flyout — portalled to document.body so it can escape
          the sidebar's overflow: hidden. Positioned at the trigger icon's
          right edge (no gap) so mouse-transit doesn't drop the hover. */}
      {collapsed && hoverState && hoveredGroup && createPortal(
        <div
          className={styles.flyout}
          style={{ top: hoverState.top, left: hoverState.left }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          role="menu"
          aria-label={hoveredGroup.label}
        >
          {hoveredGroup.items && hoveredGroup.items.length > 0 ? (
            <>
              <div className={styles.flyoutHead}>{hoveredGroup.label}</div>
              <div className={styles.flyoutItems}>
                {hoveredGroup.items.map((item) =>
                  item.soon ? (
                    <span
                      key={item.id}
                      className={`${styles.flyoutItem} ${styles.itemSoon}`}
                      title="Coming soon"
                    >
                      <span className={styles.flyoutIc}>
                        <i className={`fas ${item.icon}`} />
                      </span>
                      <span className={styles.flyoutText}>{item.label}</span>
                      <span className={styles.soonTag}>Soon</span>
                    </span>
                  ) : (
                    <NavLink
                      key={item.id}
                      to={item.href}
                      className={({ isActive: a }) =>
                        `${styles.flyoutItem} ${a ? styles.flyoutItemActive : ""}`
                      }
                      onMouseEnter={() => preloadRoute(item.href)}
                      onFocus={() => preloadRoute(item.href)}
                      onClick={() => setHoverState(null)}
                      role="menuitem"
                    >
                      <span className={styles.flyoutIc}>
                        <i className={`fas ${item.icon}`} />
                      </span>
                      <span className={styles.flyoutText}>{item.label}</span>
                    </NavLink>
                  ),
                )}
              </div>
            </>
          ) : (
            // Direct link (no sub-items) — show a simple label pill; the
            // trigger IS the icon, so no need to repeat it here.
            <div className={styles.flyoutSolo}>{hoveredGroup.label}</div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};
